import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LayoutHeader from '../../layout/LayoutHeader';
import { 
  remindersManager, 
  getActiveReminders, 
  getUnviewedRemindersCount,
  getCompletedReminders,
  completeReminder,
  revertReminder,
  deleteReminder,
  snoozeReminder,
  markReminderViewed,
  createRemindersForRelease,
  REMINDER_TEMPLATES 
} from '../../utils/remindersData';
import { useDispatch } from 'react-redux';
import { setSuccessAlert, setErrorAlert } from '../../store/alert/alertSlice';
// import ReminderNotificationModal from '../../components/ReminderNotificationModal'; // REMOVED - buggy modal
// import { useReminderNotifications } from '../../hooks/useReminderNotifications'; // REMOVED - buggy modal

const RemindersApp = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overdue'); // Changed default to overdue since it's now first
  const [refreshKey, setRefreshKey] = useState(0); // Force re-render key
  const [isLoadingReminders, setIsLoadingReminders] = useState(false); // Prevent infinite loops
  const [showCleanupPrompt, setShowCleanupPrompt] = useState(false);
  const [oldRemindersCount, setOldRemindersCount] = useState(0);
  // Removed modal state - now using dedicated page

  // Check for old reminders on mount and offer cleanup
  useEffect(() => {
    const checkForOldReminders = () => {
      const allReminders = remindersManager.getAllReminders();
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      const oldCount = allReminders.filter(reminder => {
        const dueDate = new Date(reminder.dueDate);
        return reminder.status !== 'completed' && dueDate < sixMonthsAgo;
      }).length;
      
      // Show cleanup prompt if more than 50 old reminders
      if (oldCount > 50) {
        setOldRemindersCount(oldCount);
        setShowCleanupPrompt(true);
      }
    };
    
    checkForOldReminders();
  }, []);

  // Load reminders on component mount
  useEffect(() => {
    loadReminders(true); // Pass true for initial load
  }, [activeTab]); // Add activeTab dependency to reload when tab changes

  // Listen for localStorage changes to keep reminders in sync
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'dizee_reminders' || e.key === 'catalog_updated') {
        console.log(`📡 Storage change detected (${e.key}), reloading reminders...`);
        // Force reload from localStorage and trigger reminder system check
        remindersManager.reloadReminders();
        if (e.key === 'catalog_updated') {
          // Catalog was updated, check for release date changes
          remindersManager.checkForNewReleases();
        }
        loadReminders(false); // Don't mark as viewed on storage changes
      }
    };

    // Listen for storage events from other tabs/components
    window.addEventListener('storage', handleStorageChange);

    // Also listen for our custom storage events from same tab
    const handleCustomStorageEvent = (e) => {
      if (e.detail?.key === 'dizee_reminders' || e.detail?.key === 'catalog_updated') {
        console.log(`📡 Custom storage event detected (${e.detail.key}), reloading reminders...`);
        // Force reload from localStorage
        remindersManager.reloadReminders();
        if (e.detail?.key === 'catalog_updated') {
          // Catalog was updated, check for release date changes
          remindersManager.checkForNewReleases();
        }
        loadReminders(false); // Don't mark as viewed on storage changes
      }
    };

    // Listen for overdue reminder checks
    const handleOverdueCheck = () => {
      console.log('🔔 Overdue check triggered in RemindersApp, reloading...');
      loadReminders(false); // Don't mark as viewed on overdue checks
    };

    window.addEventListener('localStorageUpdate', handleCustomStorageEvent);
    window.addEventListener('checkOverdueReminders', handleOverdueCheck);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdate', handleCustomStorageEvent);
      window.removeEventListener('checkOverdueReminders', handleOverdueCheck);
    };
  }, []);

  const loadReminders = (shouldMarkViewed = false) => {
    // Prevent infinite loops
    if (isLoadingReminders) {
      console.log('⏸️ Already loading reminders, skipping...');
      return;
    }

    try {
      setIsLoadingReminders(true);
      setLoading(true);
      
      // Force reload from localStorage to ensure latest data
      remindersManager.reloadReminders();
      
      // Get all reminders with new array reference for React updates
      let allReminders = remindersManager.getAllReminders();
      
      // PERFORMANCE SAFEGUARD: Filter out extremely old overdue reminders (more than 6 months overdue)
      // This prevents the app from crashing when bulk importing old shows
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      
      const totalCount = allReminders.length;
      allReminders = allReminders.filter(reminder => {
        const dueDate = new Date(reminder.dueDate);
        // Keep reminder if it's not completed and not extremely overdue
        if (reminder.status !== 'completed' && dueDate < sixMonthsAgo) {
          console.log(`⚠️ Filtering out extremely old reminder: ${reminder.releaseName} (due: ${dueDate.toLocaleDateString()})`);
          return false;
        }
        return true;
      });
      
      if (allReminders.length < totalCount) {
        console.log(`🧹 Filtered out ${totalCount - allReminders.length} extremely old reminders to prevent performance issues`);
        dispatch(setSuccessAlert(`Filtered ${totalCount - allReminders.length} very old reminders. Consider archiving old shows.`));
      }
      
      // LIMIT: Cap at 500 reminders to prevent UI freeze
      if (allReminders.length > 500) {
        console.warn(`⚠️ Too many reminders (${allReminders.length}). Limiting to 500 most recent.`);
        // Sort by due date and take the 500 most recent
        allReminders = allReminders
          .sort((a, b) => new Date(b.dueDate) - new Date(a.dueDate))
          .slice(0, 500);
        dispatch(setErrorAlert(`Showing 500 most recent reminders. Please archive old shows to improve performance.`));
      }
      
      setReminders([...allReminders]); // Force new array reference
      setRefreshKey(prev => prev + 1); // Force re-render
      
      console.log(`📋 Loaded ${allReminders.length} reminders`);
      
      // Only mark as viewed on initial load, not on storage change updates
      // Also limit to prevent performance issues
      if (shouldMarkViewed) {
        const activeReminders = getActiveReminders();
        const remindersToMark = activeReminders.slice(0, 100); // Limit marking to first 100
        remindersToMark.forEach(reminder => {
          if (!reminder.viewed) {
            markReminderViewed(reminder.id);
          }
        });
      }
    } catch (error) {
      console.error('Error loading reminders:', error);
      dispatch(setErrorAlert('Failed to load reminders'));
    } finally {
      setLoading(false);
      setIsLoadingReminders(false);
    }
  };

  const handleCompleteReminder = (reminderId) => {
    try {
      const success = completeReminder(reminderId);
      if (success) {
        // Force immediate state update with new array reference
        const allReminders = remindersManager.getAllReminders();
        setReminders([...allReminders]); // Spread to create new array reference
        setRefreshKey(prev => prev + 1); // Force component re-render
        
        dispatch(setSuccessAlert('Reminder marked as completed!'));
      } else {
        dispatch(setErrorAlert('Failed to complete reminder'));
      }
    } catch (error) {
      console.error('Error completing reminder:', error);
      dispatch(setErrorAlert('Failed to complete reminder'));
    }
  };

  const handleRevertReminder = (reminderId) => {
    try {
      const success = revertReminder(reminderId);
      if (success) {
        // Find the reverted reminder to determine which tab it should appear in
        const allReminders = remindersManager.getAllReminders();
        const revertedReminder = allReminders.find(r => r.id === reminderId);
        
        if (revertedReminder) {
          // Determine the appropriate tab for the reverted reminder
          let targetTab = 'all';
          if (revertedReminder.isOverdue()) {
            targetTab = 'overdue';
          } else if (revertedReminder.template?.priority === 'critical') {
            targetTab = 'urgent';
          } else if (revertedReminder.isDueToday()) {
            targetTab = 'today';
          }
          
          // Switch to the appropriate tab so user can see the reminder
          setActiveTab(targetTab);
        }
        
        // Force immediate state update
        setReminders([...allReminders]);
        setRefreshKey(prev => prev + 1);
        
        dispatch(setSuccessAlert('Reminder reverted to pending!'));
      } else {
        dispatch(setErrorAlert('Failed to revert reminder'));
      }
    } catch (error) {
      console.error('Error reverting reminder:', error);
      dispatch(setErrorAlert('Failed to revert reminder'));
    }
  };

  const handleSnoozeReminder = (reminderId, hours = 24) => {
    try {
      const success = snoozeReminder(reminderId, hours);
      if (success) {
        // Force immediate state update
        const allReminders = remindersManager.getAllReminders();
        setReminders([...allReminders]);
        setRefreshKey(prev => prev + 1);
        
        dispatch(setSuccessAlert(`Reminder snoozed for ${hours} hours`));
      } else {
        dispatch(setErrorAlert('Failed to snooze reminder'));
      }
    } catch (error) {
      console.error('Error snoozing reminder:', error);
      dispatch(setErrorAlert('Failed to snooze reminder'));
    }
  };

  const handleCreateQuickReminder = () => {
    navigate('/reminders/quick-add');
  };

  const handleDeleteReminder = (reminderId) => {
    try {
      const success = deleteReminder(reminderId);
      if (success) {
        // Force immediate state update
        const allReminders = remindersManager.getAllReminders();
        setReminders([...allReminders]);
        setRefreshKey(prev => prev + 1);
        
        dispatch(setSuccessAlert('Reminder deleted permanently!'));
      } else {
        dispatch(setErrorAlert('Failed to delete reminder'));
      }
    } catch (error) {
      console.error('Error deleting reminder:', error);
      dispatch(setErrorAlert('Failed to delete reminder'));
    }
  };

  const handleBulkCleanup = () => {
    try {
      const deletedCount = remindersManager.bulkDeleteOldReminders(6);
      setShowCleanupPrompt(false);
      
      if (deletedCount > 0) {
        dispatch(setSuccessAlert(`Cleaned up ${deletedCount} old reminders to improve performance`));
        // Reload reminders after cleanup
        loadReminders(false);
      } else {
        dispatch(setSuccessAlert('No old reminders found to clean up'));
      }
    } catch (error) {
      console.error('Error during bulk cleanup:', error);
      dispatch(setErrorAlert('Failed to clean up old reminders'));
    }
  };

  // Function to get display name for a reminder - SIMPLIFIED
  const getReminderDisplayName = (reminder) => {
    // Since we now store song names directly, this is much simpler
    return reminder.releaseName || 'Untitled Release';
  };

  // Tab filtering - Updated structure per requirements
  const tabs = [
    { id: 'overdue', label: 'Overdue' },
    { id: 'urgent', label: 'Urgent' },
    { id: 'today', label: 'Due Today' },
    { id: 'completed', label: 'Completed' },
    { id: 'all', label: 'All' }
  ];

  const filteredReminders = reminders.filter(reminder => {
    switch (activeTab) {
      case 'overdue':
        // Consolidate overdue with what was previously critical
        const isOverdue = reminder.isOverdue && reminder.isOverdue();
        
        // Debug logging for overdue tab (only when debugging)
        if (window.debugRemindersFiltering) {
          console.log(`🔍 Filtering reminder "${reminder.template?.title}":`, {
            isOverdue: isOverdue,
            status: reminder.status,
            dueDate: reminder.dueDate,
            viewed: reminder.viewed,
            hasMethod: typeof reminder.isOverdue === 'function',
            willShow: isOverdue
          });
        }
        
        if (!isOverdue && reminder.status === 'pending') {
          // Log potential issues with overdue detection
          console.log('⚠️ Reminder not showing as overdue:', {
            title: reminder.template?.title,
            dueDate: reminder.dueDate,
            status: reminder.status,
            hasMethod: typeof reminder.isOverdue === 'function'
          });
        }
        return isOverdue;
      case 'urgent':
        // Renamed from critical - high priority items (including overdue critical items)
        return reminder.template?.priority === 'critical';
      case 'today':
        return reminder.isDueToday && reminder.isDueToday();
      case 'completed':
        return reminder.status === 'completed';
      default:
        return true;
    }
  });

  // Only log when there's a mismatch
  if (activeTab === 'overdue' && filteredReminders.length === 0) {
    console.log('🚨 No overdue reminders found. Total reminders:', reminders.length);
  }

  // Priority colors
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return '#FF0000';
      case 'high': return '#FF3B30';
      case 'medium': return '#34C759';
      default: return '#007AFF';
    }
  };

  const formatTimeUntilDue = (reminder) => {
    // Don't show time info for completed reminders
    if (reminder.status === 'completed') {
      return 'Completed';
    }
    
    const days = reminder.getDaysUntilDue();
    if (days < 0) {
      return `${Math.abs(days)} days overdue`;
    } else if (days === 0) {
      return 'Due today';
    } else if (days === 1) {
      return 'Due tomorrow';
    } else {
      return `Due in ${days} days`;
    }
  };

  if (loading) {
    return (
      <LayoutHeader>
        <div className="flex justify-center w-full">
          <div className="w-[390px] h-[80vh] flex flex-col items-center justify-center">
            <div className="w-full px-4">
              <div className="text-white text-[16px] font-bold mb-2 text-left">
                Loading reminders...
              </div>
              <div className="text-[rgba(255,255,255,0.7)] text-[12px] mb-4 max-w-[300px]">
                Fetching your active reminders and release workflow tasks...
              </div>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]"></div>
              </div>
            </div>
          </div>
        </div>
      </LayoutHeader>
    );
  }

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Reminders</span>
            <div className="flex items-center gap-4">
              <button 
                onClick={handleCreateQuickReminder}
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70 flex items-center gap-1"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Quick Reminder
              </button>
              <span className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" onClick={() => navigate(-1)}>Go back</span>
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <div className="flex overflow-x-auto scrollbar-hide px-4">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-shrink-0 px-4 py-2 text-[12px] transition-colors relative ${
                    activeTab === tab.id 
                      ? 'text-white' 
                      : 'text-[rgba(255,255,255,0.5)] hover:text-white'
                  }`}
                >
                  {tab.label}
                  {activeTab === tab.id && (
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#FF3B30]"></div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div key={refreshKey} className="w-full px-4 flex-1 overflow-y-auto">
            {filteredReminders.length === 0 ? (
              <div className="text-left py-[60px]">
                <h3 className="text-white text-[12px] font-semibold mb-2">
                  No reminders found
                </h3>
                <p className="text-[rgba(255,255,255,0.5)] text-[12px] leading-[1.5] mb-6 max-w-[300px]">
                  {activeTab === 'all' 
                    ? "You're all caught up! The system automatically creates reminders for releases in your catalog. You can also create quick reminders for anything else."
                    : `No ${activeTab} reminders found. Try switching to a different tab.`
                  }
                </p>
                <button 
                  onClick={handleCreateQuickReminder}
                  className="bg-[#FF3B30] text-white px-5 py-2 rounded-lg text-[12px] hover:bg-[#FF3B30CC] transition-colors"
                >
                  Create Quick Reminder
                </button>
              </div>
            ) : (
              <div className="grid gap-4">
                {filteredReminders.map((reminder, index) => (
                  <div 
                    key={reminder.id}
                    className="bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] rounded-lg p-4 hover:border-[#FF3B30] hover:bg-[rgba(255,59,48,0.05)] transition-all"
                  >
                    <div className="w-full">
                      {/* Reminder Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <h3 className="text-white text-[14px] font-semibold break-words">
                            {reminder.template?.title}
                          </h3>
                        </div>
                        <div 
                          className="text-[10px] text-right ml-2"
                          style={{
                            color: reminder.status === 'completed' 
                              ? '#34C759' // Green for completed
                              : (reminder.isOverdue && reminder.isOverdue() ? '#FF2300' : 'rgba(255,255,255,0.5)')
                          }}
                        >
                          {formatTimeUntilDue(reminder)}
                        </div>
                      </div>
                        
                        <p className="text-[rgba(255,255,255,0.7)] text-[12px] mb-2 break-words">
                          <span className="bg-[rgba(23,162,184,0.2)] text-[#17A2B8] text-[9px] px-2 py-1 rounded font-medium">
                            {getReminderDisplayName(reminder)}
                          </span>
                        </p>
                        
                        <p className="text-[rgba(255,255,255,0.6)] text-[11px] mb-3 leading-tight break-words">
                          {reminder.template?.description}
                        </p>

                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-3">
                          {/* Show revert and delete buttons for all completed reminders */}
                          {reminder.status === 'completed' && (
                            <>
                              <button
                                onClick={() => handleRevertReminder(reminder.id)}
                                className="bg-orange-500 text-white px-3 py-1 rounded text-[10px] hover:bg-orange-600 transition-colors"
                              >
                                Revert
                              </button>
                              <button
                                onClick={() => handleDeleteReminder(reminder.id)}
                                className="bg-red-500 text-white px-3 py-1 rounded text-[10px] hover:bg-red-600 transition-colors"
                              >
                                Delete
                              </button>
                            </>
                          )}
                          
                          {/* Show completion button for all pending reminders */}
                          {reminder.status !== 'completed' && (
                            <>
                              <button
                                onClick={() => handleCompleteReminder(reminder.id)}
                                className="bg-green-600 text-white px-3 py-1 rounded text-[10px] hover:bg-green-700 transition-colors"
                              >
                                Completed
                              </button>
                              
                              {reminder.template?.actions?.includes('Remind Me Later') && (
                                <button
                                  onClick={() => handleSnoozeReminder(reminder.id, 24)}
                                  className="bg-[rgba(255,255,255,0.1)] text-white px-3 py-1 rounded text-[10px] hover:bg-[rgba(255,255,255,0.2)] transition-colors"
                                >
                                  Remind Later
                                </button>
                              )}

                              {reminder.template?.actions?.includes('Generate Report') && (
                                <button
                                  onClick={() => {
                                    // Could integrate with analytics apps
                                    navigate('/analytics');
                                  }}
                                  className="bg-[#FF3B30] text-white px-3 py-1 rounded text-[10px] hover:bg-[#FF3B30CC] transition-colors"
                                >
                                  📊 View Analytics
                                </button>
                              )}
                            </>
                          )}
                        </div>

                      {/* Metadata - REMOVED */}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* Cleanup Prompt Modal */}
      {showCleanupPrompt && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 px-4">
          <div className="bg-[#1A1A1A] rounded-2xl p-6 max-w-[340px] w-full border border-[rgba(255,255,255,0.1)]">
            <div className="mb-4">
              <h3 className="text-white text-[16px] font-semibold mb-2">
                🧹 Performance Optimization
              </h3>
              <p className="text-[rgba(255,255,255,0.7)] text-[13px] leading-[1.5]">
                We found <strong className="text-white">{oldRemindersCount} very old reminders</strong> (over 6 months overdue) that may be slowing down the app.
              </p>
              <p className="text-[rgba(255,255,255,0.7)] text-[13px] leading-[1.5] mt-2">
                Would you like to automatically clean these up? This will remove old incomplete reminders while keeping completed ones.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowCleanupPrompt(false)}
                className="flex-1 bg-[rgba(255,255,255,0.1)] text-white px-4 py-3 rounded-lg text-[13px] font-medium hover:bg-[rgba(255,255,255,0.15)] transition-colors"
              >
                Keep All
              </button>
              <button
                onClick={handleBulkCleanup}
                className="flex-1 bg-[#FF3B30] text-white px-4 py-3 rounded-lg text-[13px] font-medium hover:bg-[#FF3B30CC] transition-colors"
              >
                Clean Up Now
              </button>
            </div>
          </div>
        </div>
      )}
    </LayoutHeader>
  );
};

export default RemindersApp; 